import { supabase } from '@/integrations/supabase/client';
import { pushClientError } from '@/utils/clientErrorBuffer';
// DTC-J: route the technician→passport sync `updated_at` audit-trail
// writes through the canonical clock so audit timestamps are
// deterministic under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';

/**
 * Belt-and-suspenders sync helper: pushes live shop technician data to
 * passport snapshot tables.
 *
 * Syncs: full_name, is_active, and all proficiency categories.
 *
 * AUTHORITY (DMC-F):
 *   The CANONICAL sync path is the SECURITY DEFINER trigger
 *   `sync_technician_to_passport_trigger` attached AFTER INSERT OR
 *   UPDATE on `technicians` (migration
 *   `20260509120000_dmc_f_passport_sync_trigger.sql`). The trigger
 *   mirrors this helper's writes server-side and bypasses RLS on the
 *   passport tables. As of DMC-F, every successful technician
 *   mutation produces a synced passport snapshot regardless of caller
 *   role — manager edits no longer silently drift.
 *
 *   This helper is preserved at its 4 call sites (TechnicianForm,
 *   TicketCard, TeamManagement, TechnicianEditor) as:
 *     1. Belt-and-suspenders: redundant client-side write that
 *        succeeds for owner/service-role callers and is harmlessly
 *        denied for manager callers (the trigger has already done the
 *        write server-side, so a client-side denial is no longer a
 *        correctness issue).
 *     2. Client-observability: RLS-denied writes still flow to the
 *        client-error buffer, providing a fast signal if the trigger
 *        is ever disabled or regresses.
 *
 * HISTORY (FIX-R133-MED-1 / R37, closed by DMC-F):
 *   Pre-DMC-F, this helper ran from the manager's client context and
 *   the user-owned passport tables had RLS that rejected manager
 *   writes:
 *     - `technician_profiles` UPDATE: owner-only OR service-role.
 *     - `technician_proficiencies` ALL: service-role-only.
 *   So both the .update and .upsert were SILENTLY DENIED by RLS,
 *   leaving the passport snapshot unsynced for every manager-driven
 *   edit. This was tracked as PROD-D / R37 with the originally-
 *   recommended fix being an edge-function with service-role. The
 *   DMC-F server-side trigger achieves the same goal more directly
 *   and closes the bug.
 *
 * @param technicianId - The shop technician row ID
 * @param options.forceInactive - DEPRECATED. Repo-wide grep confirms zero
 *   callers pass `forceInactive: true`; the option is dead. The DMC-F
 *   trigger reads `employment_status` directly with no override path.
 */
export async function syncTechnicianToPassport(
  technicianId: string,
  options?: { forceInactive?: boolean }
): Promise<void> {
  try {
    // Read the live technician row
    const { data: tech, error: techError } = await supabase
      .from('technicians')
      .select('name, employment_status, proficiencies, technician_profile_id, shop_id')
      .eq('id', technicianId)
      .single();

    if (techError || !tech?.technician_profile_id) {
      console.log(`[SyncToPassport] No profile linked for tech ${technicianId}, skipping`);
      return;
    }

    const profileId = tech.technician_profile_id;
    const shopId = (tech as { shop_id?: string }).shop_id ?? null;
    const isActive = options?.forceInactive ? false : tech.employment_status === 'Active';

    // Update technician_profiles snapshot
    const { error: profileErr } = await supabase
      .from('technician_profiles')
      .update({
        full_name: tech.name,
        is_active: isActive,
        updated_at: clock.now().toISOString(),
      })
      .eq('id', profileId);

    if (profileErr) {
      // FIX-R133-MED-1: surface silent RLS-denied sync to support buffer.
      console.error('[SyncToPassport] Failed to update profile:', profileErr);
      pushClientError({
        source: 'mutation',
        message: `[SyncToPassport] technician_profiles update silently denied (likely RLS): ${profileErr.message ?? profileErr}`,
        extra: { technicianId, profileId, expected: 'manager edit should propagate to user passport snapshot' },
        shopId,
      });
    }

    // Upsert proficiencies
    const proficiencies = (tech.proficiencies as Record<string, number>) || {};
    const categories = Object.keys(proficiencies);

    if (categories.length > 0) {
      const upsertData = categories.map(category => ({
        technician_profile_id: profileId,
        category_key: category,
        proficiency_score: Math.round(proficiencies[category]),
        // Core-100 R2 CORE-E: no fabricated confidence. The server trigger writes the authority's
        // confidence (or NULL = unknown); this belt-and-braces write leaves it to the server.
        updated_at: clock.now().toISOString(),
      }));

      const { error: upsertError } = await supabase
        .from('technician_proficiencies')
        .upsert(upsertData, {
          onConflict: 'technician_profile_id,category_key',
        });

      if (upsertError) {
        console.error('[SyncToPassport] Failed to upsert proficiencies:', upsertError);
        pushClientError({
          source: 'mutation',
          message: `[SyncToPassport] technician_proficiencies upsert silently denied (likely RLS — service-role-only): ${upsertError.message ?? upsertError}`,
          extra: { technicianId, profileId, categoryCount: categories.length },
          shopId,
        });
      }
    }

    console.log(`[SyncToPassport] Synced tech ${technicianId} → profile ${profileId} (active=${isActive})`);
  } catch (error) {
    console.error('[SyncToPassport] Error:', error);
    pushClientError({
      source: 'mutation',
      message: `[SyncToPassport] threw: ${(error as { message?: string } | undefined)?.message ?? String(error)}`,
      stack: (error as { stack?: string } | undefined)?.stack,
      extra: { technicianId },
    });
    // Don't throw — sync failures shouldn't block primary operations
  }
}
